import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Avatar, Badge, Button, Skeleton, Text } from '@repletoryreach/sdk-ui';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { useAgentOptions } from './TransferModal';

const NO_USER = '00000000-0000-0000-0000-000000000000';

/**
 * Who wrote a note, by name (owner, 30 October 2026: "instead of saying Agent it should say Anitha"). The team list
 * comes first, so a note shows the person's current name, then the name kept on the note (the chatbot's hand-over
 * notes, someone who has left the team), and "Agent" only when neither is known. Notes written before names were
 * kept still get one from the team list.
 */
export function noteAuthorName(
  note: { author_user_id?: string | null; author_name?: string | null },
  team: { user_id: string; label: string }[],
  fallback: string
): string {
  const id = note.author_user_id;
  if (id && id !== NO_USER) {
    const member = team.find((m) => m.user_id === id);
    if (member) return member.label;
  }
  return note.author_name?.trim() || fallback;
}

/**
 * Internal notes on a conversation, never sent to the contact.
 *
 * The body of the "Internal notes" section of the settings sidebar; the
 * section supplies the title. Each note names its author and carries a
 * "Private" badge, and the section opens by saying who can see them: the one
 * thing an agent must never be unsure about here is whether what they are
 * typing reaches the customer.
 *
 * Enter adds the note and Shift+Enter starts a new line, the same contract as
 * the reply box, so the one habit works in both.
 */
export function NotesPanel({ conversationId }: { conversationId: string }) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { options: team } = useAgentOptions();
  const [draft, setDraft] = useState('');

  const notes = useQuery({
    queryKey: ['conversation', conversationId, 'notes'],
    queryFn: () => inbox.listNotes(conversationId)
  });

  const addNote = useMutation({
    // The writer's name is kept on the note too, so it still reads right after they leave the team.
    mutationFn: (body: string) => inbox.addNote(conversationId, body, user?.display_name || user?.email || undefined),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'notes'] });
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.notes.addFailed'), t('inbox.notes.title'))
  });

  const noteList = notes.data?.data ?? [];

  function submit() {
    const body = draft.trim();
    if (body && !addNote.isPending) addNote.mutate(body);
  }

  return (
    <div className="rr-inbox2__notes">
      <Text role="caption" c="var(--rr-color-text-muted)">{t('inbox.notes.visibility')}</Text>

      {notes.isPending && <Skeleton height={48} />}

      {!notes.isPending && noteList.length === 0 && (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('inbox.notes.empty')}</Text>
      )}

      {noteList.length > 0 && (
        <div className="rr-inbox2__note-list">
          {noteList.map((n) => {
            const author = noteAuthorName(n, team, t('inbox.notes.agent'));
            return (
            <article key={n.id} className="rr-inbox2__note">
              <Avatar name={author} size={26} />
              <div className="rr-inbox2__note-main">
                <div className="rr-inbox2__note-head">
                  <span className="rr-inbox2__note-author">{author}</span>
                  <Badge size="xs" variant="light" color="gray">{t('inbox.notes.private')}</Badge>
                </div>
                <p className="rr-inbox2__note-body">{n.body}</p>
                <Text role="caption" c="var(--rr-color-text-muted)">{formatRelative(n.created_at)}</Text>
              </div>
            </article>
            );
          })}
        </div>
      )}

      <Textarea
        placeholder={t('inbox.notes.placeholder')}
        aria-label={t('inbox.notes.placeholder')}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        minRows={3}
        maxRows={8}
        autosize
      />
      <div className="rr-inbox2__note-actions">
        <Button
          size="xs"
          variant="default"
          loading={addNote.isPending}
          disabled={!draft.trim()}
          onClick={submit}
        >
          {t('inbox.notes.add')}
        </Button>
      </div>
    </div>
  );
}
